import { TAROT_CARD_BY_ID, TAROT_DECK } from "./tarotCatalog.js";

export const TAROT_SPREADS = {
  three_card: ["past", "present", "future"],
  celtic_cross: [
    "present",
    "challenge",
    "foundation",
    "recent_past",
    "crown",
    "near_future",
    "self",
    "environment",
    "hopes_fears",
    "outcome",
  ],
};

function hashSeed(seedText) {
  let h = 1779033703 ^ String(seedText || "").length;
  for (let i = 0; i < String(seedText || "").length; i += 1) {
    h = Math.imul(h ^ String(seedText).charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    h ^= h >>> 16;
    return h >>> 0;
  };
}

function mulberry32(a) {
  return function next() {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function seededRandom(seed) {
  const nextInt = hashSeed(seed || Date.now().toString());
  return mulberry32(nextInt());
}

function interpolate(template, vars = {}) {
  return Object.entries(vars).reduce((text, [key, value]) => {
    return String(text).replace(new RegExp(`%\\{${key}\\}`, "g"), String(value ?? ""));
  }, String(template ?? ""));
}

function lookupTranslation(t, key) {
  const value = t(key);
  if (!value || value === key) return null;
  return value;
}

function resolveCardName(card, t) {
  if (card.arcana === "major") return t(`tarot_card_name_major_${card.rankKey}`);
  const rank = t(`tarot_minor_rank_${card.rankKey}`);
  const suit = t(`tarot_minor_suit_${card.suit}`);
  return interpolate(t("tarot_minor_name_template"), { rank, suit });
}

function resolveBaseCardMeaning(card, reversed, t) {
  const orientation = reversed ? "reversed" : "upright";
  if (card.arcana === "major") {
    const key = `tarot_major_meaning_${orientation}_${card.rankKey}`;
    const specific = lookupTranslation(t, key);
    if (specific) return specific;
    const name = resolveCardName(card, t);
    const template = t(`tarot_major_meaning_${orientation}_template`);
    return interpolate(template, { name });
  }
  const rankKey = `tarot_minor_meaning_${orientation}_${card.suit}_${card.rankKey}`;
  const suitKey = `tarot_minor_meaning_${orientation}_${card.suit}`;
  return lookupTranslation(t, rankKey) || t(suitKey);
}

function resolveCardMeaning(card, reversed, t, positionKey) {
  const meaning = resolveBaseCardMeaning(card, reversed, t);
  const frame = positionKey ? lookupTranslation(t, `tarot_pos_frame_${positionKey}`) : null;
  if (frame && meaning) return interpolate(frame, { meaning });
  return meaning;
}

function resolveSynthesis({ cards, spreadKey, t }) {
  const first = cards[0];
  const family = first?.arcana === "major" ? "major" : (first?.suit || "minor");
  const specific = lookupTranslation(t, `tarot_synthesis_${spreadKey}_${family}`);
  const template = specific || t("tarot_synthesis_dynamic");
  return interpolate(template, {
    cards: cards.length,
    name: first?.name || "",
    position: first?.positionLabel || first?.positionKey || "",
  });
}

function resolveCardNumber(card, t) {
  if (card.arcana === "major") return card.number;
  const localizedRank = t(`tarot_minor_rank_${card.rankKey}`);
  const isNumericRank = ["ace", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"].includes(card.rankKey);
  if (isNumericRank) return card.number;
  return localizedRank || card.number;
}

function normalizeText(value) {
  return String(value || "").trim().toLowerCase();
}

function inferPositionKey(card) {
  const persistedKey = normalizeText(card?.position_key);
  if (persistedKey) {
    const persistedMap = {
      distant_past: "foundation",
      foundation: "foundation",
      recent_past: "recent_past",
      near_future: "near_future",
      crown: "crown",
      present: "present",
      challenge: "challenge",
      self: "self",
      environment: "environment",
      hopes_fears: "hopes_fears",
      outcome: "outcome",
      past: "past",
      future: "future",
    };
    return persistedMap[persistedKey] || String(card.position_key);
  }
  const label = normalizeText(card?.position_label);
  const map = {
    past: "past",
    present: "present",
    future: "future",
    challenge: "challenge",
    "distant past": "foundation",
    foundation: "foundation",
    "recent past": "recent_past",
    "conscious focus": "crown",
    crown: "crown",
    "near future": "near_future",
    self: "self",
    you: "self",
    environment: "environment",
    "hopes & fears": "hopes_fears",
    "hopes and fears": "hopes_fears",
    outcome: "outcome",
  };
  return map[label] || null;
}

function inferCardId(card) {
  if (card?.card_id) return String(card.card_id);
  const name = normalizeText(card?.name);
  if (!name) return null;

  const majorMatch = name.match(/^the\s+(.+)$/);
  if (majorMatch) {
    const majorTitle = majorMatch[1];
    const majorCard = TAROT_DECK.find((item) => item.arcana === "major" && normalizeText(item.id.replace("major-", "")) === normalizeText(item.rankKey));
    // explicit name fallback lookup
    const majorNameMap = {
      "fool": "major-0",
      "magician": "major-1",
      "high priestess": "major-2",
      "empress": "major-3",
      "emperor": "major-4",
      "hierophant": "major-5",
      "lovers": "major-6",
      "chariot": "major-7",
      "strength": "major-8",
      "hermit": "major-9",
      "wheel of fortune": "major-10",
      "justice": "major-11",
      "hanged man": "major-12",
      "death": "major-13",
      "temperance": "major-14",
      "devil": "major-15",
      "tower": "major-16",
      "star": "major-17",
      "moon": "major-18",
      "sun": "major-19",
      "judgement": "major-20",
      "world": "major-21",
    };
    return majorNameMap[majorTitle] || majorCard?.id || null;
  }

  const minorMatch = name.match(/^(ace|two|three|four|five|six|seven|eight|nine|ten|page|knight|queen|king)\s+of\s+(wands|cups|swords|pentacles)$/);
  if (minorMatch) {
    return `minor-${minorMatch[2]}-${minorMatch[1]}`;
  }
  return null;
}

function drawUniqueCards({ deck, count, rng }) {
  const pool = [...deck];
  const picks = [];
  for (let i = 0; i < count && pool.length > 0; i += 1) {
    const idx = Math.floor(rng() * pool.length);
    picks.push(pool[idx]);
    pool.splice(idx, 1);
  }
  return picks;
}

export function createTarotReading({
  spreadKey = "three_card",
  positionLabels = {},
  seed = `${Date.now()}`,
  deck = TAROT_DECK,
  t = (key) => key,
}) {
  const spread = TAROT_SPREADS[spreadKey] || TAROT_SPREADS.three_card;
  const rng = seededRandom(seed);
  const cards = drawUniqueCards({ deck, count: spread.length, rng }).map((baseCard, idx) => {
    const reversed = rng() >= 0.5;
    const positionKey = spread[idx];
    const meaning = resolveCardMeaning(baseCard, reversed, t, positionKey);
    return {
      cardId: baseCard.id,
      positionKey,
      positionLabel: positionLabels[positionKey] || positionKey,
      reversed,
      number: resolveCardNumber(baseCard, t),
      icon: baseCard.icon,
      name: resolveCardName(baseCard, t),
      meaning,
      arcana: baseCard.arcana,
      suit: baseCard.suit,
      rankKey: baseCard.rankKey,
    };
  });

  const synthesis = resolveSynthesis({ cards, spreadKey, t });

  return {
    spreadKey,
    seed,
    cards,
    synthesis,
  };
}

export function hydrateTarotReadingFromPayload({ payload, positionLabels = {}, t = (key) => key }) {
  const cards = Array.isArray(payload?.cards) ? payload.cards : [];
  if (!cards.length) return null;
  const hydratedCards = cards.map((card, idx) => {
      const resolvedPositionKey = inferPositionKey(card) || `slot_${idx + 1}`;
      const resolvedCardId = inferCardId(card);
      const catalogCard = resolvedCardId ? TAROT_CARD_BY_ID[resolvedCardId] : null;
      const reversed = Boolean(card?.reversed);
      return {
        cardId: resolvedCardId || catalogCard?.id || `legacy-${idx}`,
        positionKey: resolvedPositionKey,
        positionLabel: positionLabels[resolvedPositionKey] || card?.position_label || card?.position_key || `#${idx + 1}`,
        reversed,
        number: (catalogCard ? resolveCardNumber(catalogCard, t) : null) || card?.number || "",
        icon: card?.icon || catalogCard?.icon || "✦",
        // Prefer locale-derived content when we have a stable card_id, so replay respects current language.
        name: (catalogCard ? resolveCardName(catalogCard, t) : null) || card?.name || t("tarot_unknown_card"),
        meaning: (catalogCard ? resolveCardMeaning(catalogCard, reversed, t, resolvedPositionKey) : null) || card?.meaning || "",
        arcana: card?.arcana || catalogCard?.arcana || null,
        suit: card?.suit || catalogCard?.suit || null,
        rankKey: card?.rank_key || catalogCard?.rankKey || null,
      };
    });
  const localizedSynthesis = resolveSynthesis({
    cards: hydratedCards,
    spreadKey: payload?.spread_key || "three_card",
    t,
  });

  return {
    spreadKey: payload?.spread_key || "three_card",
    seed: payload?.seed || null,
    synthesis: localizedSynthesis || payload?.synthesis || t("tarot_synthesis_replay_fallback"),
    cards: hydratedCards,
  };
}
